import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { Link, useLoaderData } from "@remix-run/react";
import { RiDownloadLine, RiExternalLinkLine } from "react-icons/ri";
import type { SitemapArgs } from "remix-sitemap";
import { serverOnly$ } from "vite-env-only/macros";

import { HtmlParser } from "~/components/HtmlParser";
import { SideMenuLayout, SideMenuLayoutWrapper } from "~/components/SideMenu/SideMenu";
import { RangeCard } from "~/components/ui/cards/range-card";
import { Image } from "~/components/ui/image";
import { PageContainer } from "~/components/ui/layout/page-container";
import { PageHeading } from "~/components/ui/page-heading";
import { getBrandBySlug } from "~/models/brand.server";
import { getRanges } from "~/models/range.server";
import { getSitemapBrands } from "~/models/sitemap.server";
import { useRootMatchData } from "~/root";
import { AppError, NotFoundInApiError } from "~/utils/errors";
import { getSeoMeta } from "~/utils/seo";

export const sitemap = serverOnly$(async ({ request }: SitemapArgs) => {
  const brands = await getSitemapBrands({ request });
  if (!brands) {
    return [];
  }
  return brands.map((brand) => ({
    loc: `/nos-partenaires/${brand.slug}`,
    exclude: !brand || !brand.presentation || !(brand.presentation > 0),
  }));
});

export const meta: MetaFunction<typeof loader> = (metaArgs) => {
  const brand = metaArgs.data?.brand;
  return getSeoMeta(metaArgs, {
    title: `${brand?.title} - Nos partenaires`,
    description: `Découvrez la marque ${brand?.title} sur Powr Connect. Retrouvez toutes les informations, les engagements et les actualités de la marque.`,
  });
};

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  try {
    const brandSlug = params.brandSlug || "";
    const brand = await getBrandBySlug({ slug: brandSlug, request });
    const ranges = await getRanges({ brands: brand.slug || "", request });

    if (!brand) {
      throw new NotFoundInApiError();
    }

    return json({ brand, ranges: ranges.data, errors: null });
  } catch (error) {
    if (error instanceof AppError) {
      return json({ brand: null, ranges: null, errors: error.getAllErrors() });
    }

    throw error;
  }
};

const BrandPage = () => {
  const { brand, ranges } = useLoaderData<typeof loader>();
  const rootData = useRootMatchData();

  const categories =
    rootData?.categories?.filter((c) => c.brands.find((b) => b.slug === brand?.slug)) || null;

  const isUserConnected = rootData?.isUserConnected || false;
  const comparator = rootData?.comparator || null;

  const certifications = ranges?.map((range) => range.certifications).flat() || null;

  const breadcrumbData = [
    { name: "Nos partenaires", slug: "/nos-partenaires" },
    { name: brand?.title || "", slug: "" },
  ];

  if (!brand) {
    return (
      <h2 className="text-center">Les informations sur cette marques ne sont pas disponibles</h2>
    );
  }

  return (
    <PageContainer>
      <PageHeading
        breadcrumbData={breadcrumbData}
        headingText="Nos partenaires"
        headingTextSecondary={brand.title}
      />
      <SideMenuLayout
        websiteUrl={brand.websiteUrl || null}
        computingToolUrl={brand.computingToolUrl || null}
      >
        <SideMenuLayoutWrapper>
          <Image
            src={brand.images[0]?.url}
            alt={brand.title}
            fallbackElement={
              <p className="mb-20 flex h-40 w-64 items-center justify-center rounded-2xl border border-grey-100 object-contain shadow-md">
                {brand.title}
              </p>
            }
            className="mb-20 h-40 w-64 rounded-2xl border border-grey-100 object-contain shadow-md"
          />
          {brand.presentation.length > 0
            ? brand.presentation.map((paragraph, index) => (
                <div className="mb-20" key={paragraph.title || index}>
                  <h2 className="mb-6 text-xl font-semibold uppercase leading-normal">
                    {paragraph.title}
                  </h2>
                  <HtmlParser html={paragraph.description || ""} />
                  {paragraph.brandContentImages.length > 0 ? (
                    <div className="mt-8 grid grid-cols-3 gap-4">
                      {paragraph.brandContentImages.map((image, imageIndex) => (
                        <Image
                          key={image.filename || imageIndex}
                          src={image.url}
                          alt={image.filename}
                          className="rounded-2xl"
                        />
                      ))}
                    </div>
                  ) : null}
                  {paragraph.brandContentVideos.length > 0 ? (
                    <div className="mt-4 grid grid-cols-1 gap-4">
                      {paragraph.brandContentVideos.map((video, videoIndex) => (
                        <video key={video.filename || videoIndex} controls>
                          <track kind="captions" />
                          <source src={video.url} type="video/mp4" />
                        </video>
                      ))}
                    </div>
                  ) : null}

                  {paragraph.brandContentLinks.length > 0 ? (
                    <div className="mt-8">
                      {paragraph.brandContentLinks.map((link) => (
                        <Link
                          to={link.url}
                          className="mb-6 flex w-fit items-center gap-4 font-medium"
                          key={link.id}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <RiExternalLinkLine className="mb-[2px] h-5 w-5" />
                          {link.title || link.url}
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))
            : null}

          {brand.news.length > 0 ? (
            <div className="mb-20">
              <h2 className="mb-6 text-xl font-semibold uppercase leading-normal">
                Nouveautés et actus de la marque
              </h2>
              {brand.news.map((paragraph, index) => (
                <div className="mb-12" key={paragraph.title || index}>
                  <h3 className="mb-2 text-lg font-semibold leading-normal text-grey-700">
                    {paragraph.title}
                  </h3>
                  <HtmlParser
                    html={paragraph.description || ""}
                    // className="prose-ul:bulleted-list prose prose-p:mb-8 prose-p:!max-w-7xl prose-p:leading-normal prose-p:text-grey-700 prose-li:mt-2"
                  />
                  {paragraph.brandContentImages.length > 0 ? (
                    <div className="mt-8 grid grid-cols-3 gap-4">
                      {paragraph.brandContentImages.map((image, imageIndex) => (
                        <Image
                          key={image.filename || imageIndex}
                          src={image.url}
                          alt={image.filename}
                          className="rounded-2xl"
                        />
                      ))}
                    </div>
                  ) : null}
                  {paragraph.brandContentVideos.length > 0 ? (
                    <div className="mt-4 grid grid-cols-1 gap-4">
                      {paragraph.brandContentVideos.map((video, videoIndex) => (
                        <video key={video.filename || videoIndex} controls>
                          <track kind="captions" />
                          <source src={video.url} type="video/mp4" />
                        </video>
                      ))}
                    </div>
                  ) : null}
                  {paragraph.brandContentLinks.length > 0 ? (
                    <div className="mt-8">
                      {paragraph.brandContentLinks.map((link) => (
                        <Link
                          to={link.url}
                          className="mb-6 flex w-fit items-center gap-4 font-medium"
                          key={link.id}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <RiExternalLinkLine className="mb-[2px] h-5 w-5" />
                          {link.title || link.url}
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          {brand.whyChooseUs.length > 0 ? (
            <div className="mb-20">
              <h2 className="mb-6 text-xl font-semibold uppercase leading-normal">
                Pourquoi choisir {brand.title} ?
              </h2>
              {brand.whyChooseUs.map((paragraph, index) => (
                <div className="mb-12" key={paragraph.title || index}>
                  <h3 className="mb-2 text-lg font-semibold leading-normal text-grey-700">
                    {paragraph.title}
                  </h3>
                  <HtmlParser
                    html={paragraph.description || ""}
                    // className="prose-ul:bulleted-list prose prose-p:mb-8 prose-p:!max-w-7xl prose-p:leading-normal prose-p:text-grey-700 prose-li:mt-2"
                  />
                  {paragraph.brandContentImages.length > 0 ? (
                    <div className="mt-8 grid grid-cols-3 gap-4">
                      {paragraph.brandContentImages.map((image, imageIndex) => (
                        <Image
                          key={image.filename || imageIndex}
                          src={image.url}
                          alt={image.filename}
                          className="rounded-2xl"
                        />
                      ))}
                    </div>
                  ) : null}
                  {paragraph.brandContentVideos.length > 0 ? (
                    <div className="mt-4 grid grid-cols-1 gap-4">
                      {paragraph.brandContentVideos.map((video, videoIndex) => (
                        <video key={video.filename || videoIndex} controls>
                          <track kind="captions" />
                          <source src={video.url} type="video/mp4" />
                        </video>
                      ))}
                    </div>
                  ) : null}
                  {paragraph.brandContentLinks.length > 0 ? (
                    <div className="mt-8">
                      {paragraph.brandContentLinks.map((link) => (
                        <Link
                          to={link.url}
                          className="mb-6 flex w-fit items-center gap-4 font-medium"
                          key={link.id}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <RiExternalLinkLine className="mb-[2px] h-5 w-5" />
                          {link.title || link.url}
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          {brand.brandCreationDate ||
          brand.headOfficeCity ||
          brand.headOfficeCountry ||
          brand.factoryCity ||
          brand.factoryCountry ||
          brand.workerCount ||
          brand.workInCountriesCount ? (
            <div className="mb-20">
              <h2 className="mb-6 text-xl font-semibold uppercase leading-normal">Données clés</h2>
              <ul className="bulleted-list mb-12">
                {brand.brandCreationDate ? (
                  <li className="mb-6 flex items-start gap-4">
                    <span className="basis-48 font-medium">Date de création : </span>
                    <span>{new Date(brand.brandCreationDate).getFullYear()}</span>
                  </li>
                ) : null}
                {brand.headOfficeCity || brand.headOfficeCountry ? (
                  <li className="mb-6 flex items-start gap-4">
                    <span className="basis-48 font-medium">Siège : </span>
                    <span>
                      {brand.headOfficeCity} ({brand.headOfficeCountry.label})
                    </span>
                  </li>
                ) : null}
                {brand.factoryCity || brand.factoryCountry ? (
                  <li className="mb-6 flex items-start gap-4">
                    <span className="basis-48 font-medium">Site de production : </span>
                    <span>
                      {brand.factoryCity} ({brand.factoryCountry.label})
                    </span>
                  </li>
                ) : null}
                {brand.workerCount ? (
                  <li className="mb-6 flex items-start gap-4">
                    <span className="basis-48 font-medium">Nombre de salariés : </span>
                    <span>{brand.workerCount}</span>
                  </li>
                ) : null}
                {brand.workInCountriesCount ? (
                  <li className="mb-6 flex items-start gap-4">
                    <span className="basis-48 font-medium">Présente dans :</span>
                    <span>{brand.workInCountriesCount} pays</span>
                  </li>
                ) : null}
              </ul>
            </div>
          ) : null}

          {brand.brandDocuments.length > 0 || brand.links.length > 0 ? (
            <div id="downloads" className="mb-20 grid grid-cols-1 gap-10 md:grid-cols-2">
              {brand.brandDocuments &&
              Array.isArray(brand.brandDocuments) &&
              brand.brandDocuments.length > 0 ? (
                <section>
                  <h2 className="mb-6 text-xl font-semibold uppercase leading-normal">
                    Documents à télécharger
                  </h2>
                  {brand.brandDocuments.map((doc) => (
                    <Link
                      to={doc.url}
                      className="mb-6 flex w-fit items-center gap-4 font-medium"
                      key={doc.filename}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <RiDownloadLine className="mb-[2px] h-5 w-5 shrink-0" />
                      {doc.filename || doc.url}
                    </Link>
                  ))}
                </section>
              ) : null}
              {brand.links && Array.isArray(brand.links) && brand.links.length > 0 ? (
                <section>
                  <h2 className="mb-6 text-xl font-semibold uppercase leading-normal">
                    Autres liens
                  </h2>
                  {brand.links.map((link) => (
                    <Link
                      to={link.url}
                      className="mb-6 flex w-fit items-center gap-4 font-medium"
                      key={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <RiExternalLinkLine className="mb-[2px] h-5 w-5 shrink-0" />
                      {link.title}
                    </Link>
                  ))}
                </section>
              ) : null}
            </div>
          ) : null}

          {categories && categories.length > 0 ? (
            <div className="mb-20">
              <h2 className="mb-6 text-xl font-semibold uppercase leading-normal">
                Voir les produits {brand.title}
              </h2>
              <ul>
                {categories.map((category) => (
                  <li key={category.id} className="mb-4">
                    <Link
                      className="text-medium underline hover:text-grey-500"
                      to={`/${category.slug}?marques=${brand.slug}`}
                    >
                      {category.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {ranges && ranges.length > 0 ? (
            <div className="mb-20">
              <h2 className="mb-6 text-xl font-semibold uppercase leading-normal">
                Gammes {brand.title}
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {ranges.map((range) => (
                  <RangeCard
                    range={range}
                    comparator={comparator}
                    isUserConnected={isUserConnected}
                    key={range.id}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {certifications && certifications.length > 0 ? (
            <div className="mb-20">
              <h2 className="mb-6 text-xl font-semibold uppercase leading-normal">
                Certifications
              </h2>
              <ul className="bulleted-list">
                {certifications.map((certification) => (
                  <li key={certification.id} className="text-medium underline hover:text-grey-500">
                    {certification.type} - {certification.title}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </SideMenuLayoutWrapper>
      </SideMenuLayout>
    </PageContainer>
  );
};

export default BrandPage;
